import { useEffect, useState } from "react";
import { Spinner } from "@/components/common/Spinner";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Icon } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { CreateSharedVolumeDialog } from "@/components/volumes/CreateSharedVolumeDialog";
import { SharedVolumeRow } from "@/components/volumes/SharedVolumeRow";
import { RESOURCE_LIST_MAX_WIDTH, ResourceList } from "@/components/common/ResourceCard";
import { deleteSharedVolume, listSharedVolumes, type SharedVolume } from "@/lib/api";
import { SHARED_VOLUME_POLL_INTERVAL_MS, isSharedVolumeTransitional } from "@/lib/shared-volumes";
import { useToastStore } from "@/stores/useToastStore";
import { useHasPermission } from "@/lib/permissions";

/**
 * 組織の共有ボリューム一覧のメインビュー。作成・削除の受付後は実体の用意・破棄が
 * バックグラウンドで進むため、作成中・削除中のものがある間だけ一覧をポーリングする。
 */
export function VolumesPage() {
	const canManageSharedVolumes = useHasPermission("manageSharedVolumes");
	const [volumes, setVolumes] = useState<SharedVolume[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [createOpen, setCreateOpen] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<SharedVolume | null>(null);

	useEffect(() => {
		let cancelled = false;
		// 初回のみスピナーを出す。更新・ポーリングでは一覧を出したまま静かに差し替える。
		if (refreshKey === 0) setLoading(true);
		listSharedVolumes()
			.then((data) => {
				if (cancelled) return;
				setVolumes(data);
				setError(null);
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [refreshKey]);

	const transitional = volumes.some((v) => isSharedVolumeTransitional(v.state));
	useEffect(() => {
		if (!transitional) return;
		const timer = setInterval(() => setRefreshKey((k) => k + 1), SHARED_VOLUME_POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [transitional]);

	const handleDeleteConfirm = async () => {
		if (!pendingDelete) return;
		// マウント中の環境があるとサーバーが 409 で拒否する。失敗はそのまま伝播させ、
		// ConfirmDialog がその理由をダイアログ内に表示する。
		await deleteSharedVolume(pendingDelete.id);
		useToastStore.getState().show({
			type: "success",
			message: `共有ボリューム「${pendingDelete.name}」の削除を開始しました。`,
		});
		setPendingDelete(null);
		setRefreshKey((k) => k + 1);
	};

	return (
		<div
			data-testid="volumes-page"
			style={{
				display: "flex",
				flexDirection: "column",
				padding: 32,
				height: "100%",
				overflowY: "auto",
				background: "var(--surface-1)",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					height: 40,
					flexShrink: 0,
					marginBottom: 24,
				}}
			>
				<h2
					style={{
						flexShrink: 0,
						whiteSpace: "nowrap",
						fontSize: "var(--text-title)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						color: "var(--fg-1)",
						margin: 0,
					}}
				>
					共有ボリューム
				</h2>
				<div
					style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, marginLeft: "auto" }}
				>
					<IconButton
						icon="rotate"
						title="一覧を更新"
						data-testid="volumes-refresh"
						size={28}
						disabled={loading}
						onClick={() => setRefreshKey((k) => k + 1)}
					/>
					{canManageSharedVolumes && (
						<ConfirmButton
							label="ボリュームを作成"
							icon="plus"
							loading={false}
							variant="primary"
							size="sm"
							onClick={() => setCreateOpen(true)}
							data-testid="create-shared-volume-button"
						/>
					)}
				</div>
			</div>
			<CreateSharedVolumeDialog
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				onCreated={(created) => {
					useToastStore.getState().show({
						type: "success",
						message: `共有ボリューム「${created.name}」を作成しています。`,
					});
					setRefreshKey((k) => k + 1);
				}}
			/>
			<ConfirmDialog
				open={pendingDelete !== null}
				title="共有ボリュームを削除"
				message={`「${pendingDelete?.name}」を削除しますか？ボリューム内のデータはすべて失われ、元に戻せません。`}
				confirmLabel="削除"
				cancelLabel="キャンセル"
				onConfirm={handleDeleteConfirm}
				onCancel={() => setPendingDelete(null)}
			/>

			{error && <p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{error}</p>}
			{!error && loading && (
				<div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
					<Spinner size={16} />
				</div>
			)}
			{!error && !loading && volumes.length === 0 && (
				<div
					data-testid="volumes-empty"
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 12,
						color: "var(--fg-3)",
						fontSize: "var(--text-ui)",
					}}
				>
					<Icon name="database" size={32} />
					<p style={{ margin: 0 }}>
						共有ボリュームはありません。「ボリュームを作成」から作成できます。
					</p>
				</div>
			)}
			{!error && volumes.length > 0 && (
				<ResourceList maxWidth={RESOURCE_LIST_MAX_WIDTH}>
					{volumes.map((volume) => (
						<SharedVolumeRow
							key={volume.id}
							volume={volume}
							onDelete={() => setPendingDelete(volume)}
						/>
					))}
				</ResourceList>
			)}
		</div>
	);
}
